import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  type Theme,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { useCanWrite } from '@/access/useCanWrite';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip, type StatusTone } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { ApplicationDeclaredEgressIpsDialog } from './ApplicationDeclaredEgressIpsDialog';

interface ApplicationEgressIpsPanelProps {
  applicationCode: string;
}

/**
 * «Direcciones IP de salida» — `GET /v1/admin/applications/{id}/egress-ips`
 * plus `GET .../edge-verification`, fetched independently so a failure to
 * read the edge access control never blocks the declared/observed data. The
 * declared addresses are operational documentation: the platform itself
 * never rejects a call based on them (enforcement lives at the edge), so
 * verification can only ever downgrade a chip to «No verificado» — a failed
 * read must never claim an address is authorized.
 */
export function ApplicationEgressIpsPanel({ applicationCode }: ApplicationEgressIpsPanelProps) {
  const translate = useTranslate();
  const canWrite = useCanWrite();
  const [editOpen, setEditOpen] = useState(false);
  const egress = useApiResource(() => payApiClient.getApplicationEgressIps(applicationCode), applicationCode);
  const verification = useApiResource(
    () => payApiClient.getApplicationEdgeVerification(applicationCode),
    applicationCode,
  );

  if (egress.loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={3} />
      </Card>
    );
  }

  if (egress.error) {
    if (egress.error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.applications.egressIps.errorTitle')} body={egress.error.message} onRetry={egress.reload} />
      </Card>
    );
  }

  if (!egress.data) return null;

  const declaredIps = egress.data.declaredIps ?? [];
  const observed = egress.data.observed ?? [];
  const verified = !verification.loading && !verification.error && verification.data?.status === 'verified'
    ? verification.data
    : undefined;
  // The edge rule protects a different host (e.g. this is a test deployment
  // while the rule guards production): the authorized list does not apply
  // here, so declared addresses carry no chips and no unmatched warning —
  // that would be structural noise, not a signal.
  const notApplicable =
    !verification.loading && !verification.error && verification.data?.status === 'not_applicable';
  const verificationUnavailable = !verification.loading && verified === undefined && !notApplicable;

  function declaredTone(ip: string): { tone: StatusTone; label: string } {
    const entry = verified?.declared.find((candidate) => candidate.ip === ip);
    if (entry === undefined) {
      return { tone: 'neutral', label: translate('pay.applications.egressIps.chipUnverified') };
    }
    return entry.listed
      ? { tone: 'success', label: translate('pay.applications.egressIps.chipListed') }
      : { tone: 'danger', label: translate('pay.applications.egressIps.chipMissing') };
  }

  const monoCellSx = { fontFamily: (theme: Theme) => theme.custom.fontFamilyMono, fontSize: '0.75rem' };
  const headCellSx = (theme: Theme) => ({
    ...theme.custom.table.headCell,
    backgroundColor: theme.custom.table.headerBg,
    borderBottom: `1px solid ${theme.custom.borderStrong}`,
  });

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
      <Card sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {translate('pay.applications.egressIps.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('pay.applications.egressIps.intro')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('pay.applications.egressIps.declaredTitle')}
          </Typography>
          {declaredIps.length === 0 ? (
            <Typography variant="body2">{translate('pay.applications.egressIps.declaredEmpty')}</Typography>
          ) : (
            <Stack spacing={1}>
              {declaredIps.map((ip) => {
                const chip = notApplicable ? undefined : declaredTone(ip);
                return (
                  <Stack key={ip} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="body2" sx={monoCellSx}>
                      {ip}
                    </Typography>
                    {chip !== undefined && <StatusChip label={chip.label} tone={chip.tone} />}
                  </Stack>
                );
              })}
            </Stack>
          )}
          {notApplicable && (
            <Typography variant="body2" color="text.secondary">
              {translate('pay.applications.egressIps.notApplicableNote')}
            </Typography>
          )}
          {verificationUnavailable && (
            <Typography variant="body2" color="text.secondary">
              {translate('pay.applications.egressIps.verificationUnavailable')}
            </Typography>
          )}
          {verified !== undefined && verified.unmatchedListEntries.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {translate('pay.applications.egressIps.unmatchedTitle')}
              </Typography>
              {verified.unmatchedListEntries.map((ip) => (
                <Typography key={ip} variant="body2" sx={monoCellSx}>
                  {ip}
                </Typography>
              ))}
            </Alert>
          )}
          {canWrite && (
            <Stack direction="row" sx={{ pt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setEditOpen(true)}>
                {translate('pay.applications.egressIps.editAction')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>

      {observed.length === 0 ? (
        <Card>
          <EmptyState
            title={translate('pay.applications.egressIps.observedEmptyTitle')}
            body={translate('pay.applications.egressIps.observedEmptyBody', { days: egress.data.windowDays })}
          />
        </Card>
      ) : (
        <Card sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table size="small">
              <caption style={{ captionSide: 'bottom' }}>
                <Typography variant="caption" color="text.secondary">
                  {translate('pay.applications.egressIps.observedCaption', { days: egress.data.windowDays })}
                </Typography>
              </caption>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>{translate('pay.applications.egressIps.columnIp')}</TableCell>
                  <TableCell sx={headCellSx}>{translate('pay.applications.egressIps.columnCredential')}</TableCell>
                  <TableCell sx={headCellSx} align="right">{translate('pay.applications.egressIps.columnCalls')}</TableCell>
                  <TableCell sx={headCellSx}>{translate('pay.applications.egressIps.columnLastSeen')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {observed.map((row) => (
                  <TableRow key={`${row.credentialId}-${row.ip}`}>
                    <TableCell sx={monoCellSx}>{row.ip}</TableCell>
                    <TableCell sx={monoCellSx}>{row.keyId}</TableCell>
                    <TableCell align="right">{row.callCount.toLocaleString('es-ES')}</TableCell>
                    <TableCell>{new Date(row.lastSeenAt).toLocaleString('es-ES')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <ApplicationDeclaredEgressIpsDialog
        open={editOpen}
        applicationCode={applicationCode}
        declaredIps={declaredIps}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          egress.reload();
          verification.reload();
        }}
      />
    </Box>
  );
}
